import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Sparkles, Plus, Pencil, Trash2, ArrowLeft, Eye, Save, Loader2, Lightbulb, Image as ImageIcon, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Guide", "Review", "Comparison", "Tutorial", "Roundup", "Developer Guide", "News"];
const EMPTY = { title: "", meta_title: "", meta_description: "", keyword: "", category: "Guide", excerpt: "", body: "" };
const today = () => new Date().toISOString().slice(0, 10);

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5A31F4] focus:border-transparent";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vr_admin_token")}` });

export const ArticlesManager = ({ onAuthFail }) => {
  const [posts, setPosts] = useState(null);
  const [view, setView] = useState("list");
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [topic, setTopic] = useState("");
  const [genKeyword, setGenKeyword] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [message, setMessage] = useState(null);
  const [publishDate, setPublishDate] = useState(today());
  const [image, setImage] = useState(null);
  const [hasExistingImage, setHasExistingImage] = useState(false);
  const [genImage, setGenImage] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const handleErr = useCallback((err, fallback) => {
    if (err.response?.status === 401) { onAuthFail(); return; }
    const d = err.response?.data?.detail;
    setMessage({ type: "error", text: typeof d === "string" ? d : fallback });
  }, [onAuthFail]);

  const loadPosts = useCallback(() => {
    axios.get(`${API}/admin/articles`, { headers: authHeaders() }).then((r) => setPosts(r.data)).catch((err) => {
      if (err.response?.status === 401) onAuthFail();
      else setPosts([]);
    });
  }, [onAuthFail]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = (prefillTopic = "", prefillKeyword = "") => {
    setForm(EMPTY); setEditingSlug(null); setTopic(prefillTopic); setGenKeyword(prefillKeyword);
    setPreview(false); setMessage(null); setPublishDate(today()); setImage(null); setHasExistingImage(false);
    setView("editor");
  };

  const openEdit = async (slug) => {
    setMessage(null);
    try {
      const { data } = await axios.get(`${API}/admin/articles/${slug}`, { headers: authHeaders() });
      setForm({ title: data.title, meta_title: data.meta_title, meta_description: data.meta_description, keyword: data.keyword, category: data.category, excerpt: data.excerpt, body: data.body });
      setPublishDate(data.published || today());
      setImage(null);
      setHasExistingImage(!!data.has_image);
      setEditingSlug(slug);
      setPreview(false);
      setView("editor");
    } catch (err) { handleErr(err, "Failed to load article."); }
  };

  const suggest = async () => {
    setSuggesting(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/admin/articles/suggest`, {}, { headers: authHeaders(), timeout: 120000 });
      setSuggestions(data);
    } catch (err) { handleErr(err, "Suggestion failed. Please try again."); }
    finally { setSuggesting(false); }
  };

  const generateImage = async () => {
    if (!form.title.trim()) { setMessage({ type: "error", text: "Add a title first — the image is generated from it." }); return; }
    setGenImage(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/admin/articles/image`, { title: form.title }, { headers: authHeaders(), timeout: 180000 });
      setImage(data.image_base64);
    } catch (err) { handleErr(err, "Image generation failed. Please try again."); }
    finally { setGenImage(false); }
  };

  const generate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setMessage(null);
    try {
      const { data } = await axios.post(`${API}/admin/articles/generate`, { topic, keyword: genKeyword || null }, { headers: authHeaders(), timeout: 180000 });
      setForm({ ...EMPTY, ...data });
      setMessage({ type: "ok", text: "Draft generated — review and edit below, then publish." });
    } catch (err) { handleErr(err, "Generation failed. Please try again."); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { setMessage({ type: "error", text: "Title and body are required." }); return; }
    setSaving(true);
    setMessage(null);
    const payload = { ...form, publish_date: publishDate || null, image_base64: image || null };
    try {
      if (editingSlug) await axios.put(`${API}/admin/articles/${editingSlug}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/articles`, payload, { headers: authHeaders() });
      loadPosts();
      setView("list");
    } catch (err) { handleErr(err, "Save failed. Please try again."); }
    finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${slug}`, { headers: authHeaders() });
      loadPosts();
    } catch (err) { handleErr(err, "Delete failed."); }
  };

  if (view === "editor") {
    return (
      <div className="mt-8" data-testid="article-editor">
        <button onClick={() => { setMessage(null); setView("list"); }} data-testid="article-back-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#1A56DB] transition-colors">
          <ArrowLeft className="w-4 h-4" /> All articles
        </button>
        <h2 className="mt-4 font-heading text-2xl font-bold text-[#0F172A]">{editingSlug ? "Edit article" : "New article"}</h2>

        {!editingSlug && (
          <div className="mt-6 bg-white border border-[#5A31F4]/30 rounded-lg p-6" data-testid="ai-generate-box">
            <div className="flex items-center gap-2 text-[#5A31F4] font-heading font-bold"><Sparkles className="w-5 h-5" /> Generate a draft with AI</div>
            <p className="mt-1 text-xs text-slate-500">Enter a topic (e.g. "AI voice for audiobook narration"). CTAs and compliance-safe editorial language are inserted automatically.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-[2fr,1fr,auto] gap-3">
              <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Article topic" data-testid="ai-topic-input" className={inputCls} />
              <input value={genKeyword} onChange={(e) => setGenKeyword(e.target.value)} placeholder="Target keyword (optional)" data-testid="ai-keyword-input" className={inputCls} />
              <button onClick={generate} disabled={generating || !topic.trim()} data-testid="ai-generate-btn" className="inline-flex items-center justify-center gap-2 bg-[#5A31F4] text-white hover:bg-[#4B29CD] disabled:opacity-50 rounded-md px-5 py-2.5 text-sm font-bold transition-all whitespace-nowrap">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing… (~1 min)</> : <><Sparkles className="w-4 h-4" /> Generate</>}
              </button>
            </div>
          </div>
        )}

        {message && <p data-testid="article-message" className={`mt-4 text-sm font-semibold ${message.type === "error" ? "text-red-600" : "text-[#16A34A]"}`}>{message.text}</p>}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Title</label>
            <input value={form.title} onChange={set("title")} data-testid="article-title-input" className={`${inputCls} mt-1`} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</label>
            <select value={form.category} onChange={set("category")} data-testid="article-category-select" className={`${inputCls} mt-1`}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Meta title (SEO)</label>
            <input value={form.meta_title} onChange={set("meta_title")} data-testid="article-metatitle-input" className={`${inputCls} mt-1`} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Target keyword</label>
            <input value={form.keyword} onChange={set("keyword")} data-testid="article-keyword-input" className={`${inputCls} mt-1`} /></div>
          <div className="lg:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Meta description (SEO)</label>
            <input value={form.meta_description} onChange={set("meta_description")} data-testid="article-metadesc-input" className={`${inputCls} mt-1`} /></div>
          <div className="lg:col-span-2"><label className="text-xs font-bold uppercase tracking-wider text-slate-500">Excerpt (shown on blog cards)</label>
            <input value={form.excerpt} onChange={set("excerpt")} data-testid="article-excerpt-input" className={`${inputCls} mt-1`} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Publish date</label>
            <input type="date" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} data-testid="article-publish-date" className={`${inputCls} mt-1`} />
            <p className="mt-1 text-xs text-slate-500">{publishDate > today() ? "Scheduled — goes live automatically on this date." : "Live immediately on save."}</p></div>
        </div>

        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-5" data-testid="article-image-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><ImageIcon className="w-4 h-4 text-[#5A31F4]" /> Header image</div>
            <button onClick={generateImage} disabled={genImage} data-testid="article-image-generate-btn" className="inline-flex items-center gap-2 border border-[#5A31F4] text-[#5A31F4] hover:bg-[#5A31F4] hover:text-white disabled:opacity-50 rounded-md px-4 py-2 text-sm font-bold transition-all">
              {genImage ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating… (~1 min)</> : <><Sparkles className="w-4 h-4" /> {image || hasExistingImage ? "Regenerate image" : "Generate header image"}</>}
            </button>
          </div>
          {image ? (
            <img src={`data:image/png;base64,${image}`} alt="Generated header preview" data-testid="article-image-preview" className="mt-4 w-full max-w-xl h-48 object-cover rounded-md border border-slate-200" />
          ) : hasExistingImage && editingSlug ? (
            <img src={`${API}/posts/${editingSlug}/image`} alt="Current header" data-testid="article-image-current" className="mt-4 w-full max-w-xl h-48 object-cover rounded-md border border-slate-200" />
          ) : (
            <p className="mt-3 text-xs text-slate-500">No image yet — AI generates an on-brand header from the article title. Saved with the article.</p>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Body (markdown)</label>
            <button onClick={() => setPreview(!preview)} data-testid="article-preview-toggle" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A56DB] hover:underline">
              <Eye className="w-4 h-4" /> {preview ? "Edit markdown" : "Preview"}
            </button>
          </div>
          {preview ? (
            <div className="mt-2 bg-white border border-slate-200 rounded-md p-6 prose prose-slate max-w-none prose-headings:font-heading" data-testid="article-preview">
              <ReactMarkdown>{form.body}</ReactMarkdown>
            </div>
          ) : (
            <textarea value={form.body} onChange={set("body")} rows={18} data-testid="article-body-input" className={`${inputCls} mt-2 font-mono text-xs leading-relaxed`} />
          )}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="article-save-btn" className="inline-flex items-center gap-2 bg-[#1A56DB] text-white hover:bg-[#1547B1] disabled:opacity-50 rounded-md px-6 py-3 text-sm font-bold transition-all">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingSlug ? "Save changes" : "Save & Publish"}
          </button>
          <span className="text-xs text-slate-500">Published immediately on the blog.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="articles-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#0F172A]">Articles ({posts ? posts.length : "…"})</h2>
        <div className="flex items-center gap-2">
          <button onClick={suggest} disabled={suggesting} data-testid="suggest-topics-btn" className="inline-flex items-center gap-2 border border-[#5A31F4] text-[#5A31F4] hover:bg-[#5A31F4] hover:text-white disabled:opacity-50 rounded-md px-5 py-2.5 text-sm font-bold transition-all">
            {suggesting ? <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</> : <><Lightbulb className="w-4 h-4" /> Suggest Topics</>}
          </button>
          <button onClick={() => openNew()} data-testid="new-article-btn" className="inline-flex items-center gap-2 bg-[#5A31F4] text-white hover:bg-[#4B29CD] rounded-md px-5 py-2.5 text-sm font-bold transition-all">
            <Plus className="w-4 h-4" /> New Article
          </button>
        </div>
      </div>
      {message && <p data-testid="article-message" className={`mt-4 text-sm font-semibold ${message.type === "error" ? "text-red-600" : "text-[#16A34A]"}`}>{message.text}</p>}

      {suggestions && (
        <div className="mt-6 border border-[#5A31F4]/30 bg-white rounded-lg p-6" data-testid="topic-suggestions">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-[#0F172A] inline-flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#5A31F4]" /> AI topic ideas {suggestions.keywords_used.length > 0 ? `— based on your paid keywords: ${suggestions.keywords_used.join(", ")}` : "— no paid keyword data yet, showing high-intent AI voice topics"}</h3>
            <button onClick={() => setSuggestions(null)} data-testid="suggestions-close-btn" className="text-sm text-slate-500 hover:text-slate-800 font-semibold">Dismiss</button>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {suggestions.suggestions.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-4 flex flex-col" data-testid={`suggestion-${i}`}>
                <p className="font-semibold text-sm text-[#0F172A]">{s.topic}</p>
                <p className="mt-1 text-xs text-slate-500">Keyword: <span className="font-semibold">{s.keyword}</span> — {s.why}</p>
                <button onClick={() => openNew(s.topic, s.keyword)} data-testid={`suggestion-use-${i}`} className="mt-3 self-start inline-flex items-center gap-1.5 text-[#5A31F4] text-sm font-bold hover:underline">
                  <Sparkles className="w-3.5 h-3.5" /> Write this article
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-6 border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left px-5 py-3 font-bold">Title</th>
              <th className="text-left px-5 py-3 font-bold">Category</th>
              <th className="text-left px-5 py-3 font-bold">Published</th>
              <th className="text-right px-5 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts === null && <tr><td colSpan="4" className="px-5 py-4 text-slate-400">Loading…</td></tr>}
            {posts && posts.map((p) => (
              <tr key={p.slug} className="border-t border-slate-100" data-testid={`article-row-${p.slug}`}>
                <td className="px-5 py-3 font-medium text-slate-700">
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="hover:text-[#1A56DB]">{p.title}</a>
                  {p.scheduled && <span className="ml-2 inline-block bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`scheduled-badge-${p.slug}`}>Scheduled</span>}
                </td>
                <td className="px-5 py-3 text-slate-500">{p.category}</td>
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{p.published}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(p.slug)} data-testid={`article-edit-${p.slug}`} className="inline-flex items-center gap-1 text-[#1A56DB] font-semibold hover:underline mr-4"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => remove(p.slug)} data-testid={`article-delete-${p.slug}`} className="inline-flex items-center gap-1 text-red-600 font-semibold hover:underline"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
